import Axios from "axios"
import {
  captureKeyEvent,
  addBreadcrumb,
  getSentryUserContext,
} from "helpers/sentryMonitoring"
import {
  encryptionController,
  EncryptionKeys,
} from "controllers/EncryptionController"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getPrivateKeyFromPem } from "helpers/utils"
import { getEncryptedKeyMiddleware } from "pages/auth/services/api"
import { updateData, getStoreData, Stores } from "services/indexedDB"
import { GetKeysType, KeyEncryptionType } from "types"
import { FailedKey } from "types/keyHealth"
interface ConversationKeyServerEntry {
  encrypted_key: string
  conversation_id: string
  additional_id: string // projectId
}

interface ProjectKeyServerEntry {
  encrypted_key: string
  type: number // KeyEncryptionType
  projectId: string
}

interface ConversationKeyResponse {
  conversationKeys: ConversationKeyServerEntry[]
  projectKeys: ProjectKeyServerEntry[]
}

interface MissingConversation {
  id: string
  code: string
  buildId: string
}

// ---------------------------------------------------------------------------
// Gate 1 — Vault Key validation + store Private Key in IndexedDB
// ---------------------------------------------------------------------------
export const checkVaultAndStorePrivateKey = async (
  userId: string
): Promise<{ vaultKeyOk: boolean }> => {
  addBreadcrumb("key-health", "Checking workspace key", "info")
  try {
    // 1a. Format check
    const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
    if (!vaultKey || !encryptionController().isValidKeyEncryption(vaultKey)) {
      return { vaultKeyOk: false }
    }

    // 1b. Fetch encrypted private key from server
    const { data: keys } = await getEncryptedKeyMiddleware({
      type: GetKeysType.PrivateKey,
    })
    if (!keys || keys.length === 0 || !keys[0]?.encrypted_key) {
      return { vaultKeyOk: false }
    }

    // 1c. Decrypt using vault key — this also verifies vault key value
    const privateKeyPem = (await encryptionGCMHelper.decryptContext(
      vaultKey,
      keys[0].encrypted_key
    )) as string

    if (!privateKeyPem || privateKeyPem === keys[0].encrypted_key) {
      return { vaultKeyOk: false }
    }

    // 1d. Store decrypted private key PEM in IndexedDB
    await updateData(Stores.PrivateKeys, {
      id: userId,
      privateKey: privateKeyPem,
    })

    return { vaultKeyOk: true }
  } catch {
    return { vaultKeyOk: false }
  }
}

// ---------------------------------------------------------------------------
// Gate 2 — Private Key availability check
// ---------------------------------------------------------------------------
export const ensurePrivateKeyExists = async (
  userId: string
): Promise<{ privateKeyOk: boolean }> => {
  try {
    const privateKeyStores = await getStoreData<{
      id: string
      privateKey: string
    }>(Stores.PrivateKeys)
    const privateKeyPem =
      privateKeyStores.find((el) => el.id === userId)?.privateKey || ""

    if (!privateKeyPem) {
      return { privateKeyOk: false }
    }

    return { privateKeyOk: true }
  } catch {
    return { privateKeyOk: false }
  }
}

// ---------------------------------------------------------------------------
// Gate 3 — Per-page project key health check
// ---------------------------------------------------------------------------
export const checkProjectKeys = async (
  userId: string,
  relationId?: string
): Promise<FailedKey[]> => {
  addBreadcrumb("key-health", "Checking project keys", "info")
  const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""

  // Get private key from IndexedDB
  const privateKeyStores = await getStoreData<{
    id: string
    privateKey: string
  }>(Stores.PrivateKeys)
  const privateKeyPem =
    privateKeyStores.find((el) => el.id === userId)?.privateKey || ""
  if (!privateKeyPem) {
    return []
  }

  const privateKeyFromPem = getPrivateKeyFromPem(privateKeyPem)

  // Fetch project keys from server
  const { data: projectKeys, missings } = await getEncryptedKeyMiddleware({
    type: GetKeysType.ProjectKey,
    ...(relationId ? { relation_id: relationId } : {}),
  })
  if ((!projectKeys || projectKeys.length === 0) && missings.length === 0) {
    return []
  }

  const localProjectKeys: Record<string, string> = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  const failedKeys: FailedKey[] = []

  // For each server key: decrypt then compare with local
  for (const entry of projectKeys || []) {
    const entryId = entry.relation_id
    const entryName = entry.name || ""
    let serverDecryptedKey: string | null = null

    if (entry.type === KeyEncryptionType.ProjectShareKey) {
      try {
        const result = privateKeyFromPem.decrypt(
          entry.encrypted_key,
          "RSA-OAEP"
        )
        if (result && result !== entry.encrypted_key) {
          serverDecryptedKey = result
        }
      } catch {
        // decrypt failed
      }
    } else {
      const result = (await encryptionGCMHelper.decryptContext(
        vaultKey,
        entry.encrypted_key
      )) as string
      if (result && result !== entry.encrypted_key) {
        serverDecryptedKey = result
      }
    }

    if (!serverDecryptedKey) {
      failedKeys.push({
        id: entryId,
        name: entryName,
        reason: "WRONG_FORMAT",
        keyType: "project",
      })
      continue
    }

    const localKey = localProjectKeys[entryId]

    if (!localKey) {
      failedKeys.push({
        id: entryId,
        name: entryName,
        reason: "MISSING",
        keyType: "project",
      })
    } else if (localKey !== serverDecryptedKey) {
      failedKeys.push({
        id: entryId,
        name: entryName,
        reason: "WRONG_VALUE",
        keyType: "project",
      })
    }
  }

  for (const mp of missings) {
    if (relationId && mp.id !== relationId) {
      continue
    }
    const hasLocalKey = !!localProjectKeys[mp.id]
    failedKeys.push({
      id: mp.id,
      name: mp.name,
      reason: hasLocalKey ? "OUT_OF_SYNC" : "NO_SERVER_KEY",
      keyType: "project",
    })
  }

  const scopedFailedKeys = relationId
    ? failedKeys.filter((k) => k.id === relationId)
    : failedKeys

  return scopedFailedKeys
}

const decryptBuildKeyValue = async (params: {
  encryptedKey: string
  additionalId?: string
  privateKeyPem: string
}): Promise<string | null> => {
  const { encryptedKey, additionalId, privateKeyPem } = params
  const projectEncryptionKeys: Record<string, string> = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  let buildKey = ""

  if (additionalId) {
    const projectKey = projectEncryptionKeys[additionalId]
    if (projectKey) {
      try {
        buildKey = (await encryptionGCMHelper.decryptContext(
          projectKey,
          encryptedKey
        )) as string
      } catch {
        buildKey = ""
      }
    }
  }

  if (!buildKey) {
    try {
      const privateKeyFromPem = getPrivateKeyFromPem(privateKeyPem)
      buildKey = privateKeyFromPem.decrypt(encryptedKey, "RSA-OAEP")
    } catch {
      buildKey = ""
    }
  }

  if (!buildKey || buildKey === encryptedKey) {
    return null
  }
  return buildKey
}

export const checkBuildKeys = async (
  userId: string,
  buildId?: string
): Promise<FailedKey[]> => {
  addBreadcrumb("key-health", "Checking build keys", "info")
  const privateKeyStores = await getStoreData<{
    id: string
    privateKey: string
  }>(Stores.PrivateKeys)
  const privateKeyPem =
    privateKeyStores.find((el) => el.id === userId)?.privateKey || ""
  if (!privateKeyPem) {
    return []
  }

  const { data: serverBuildKeys, missings: missingBuilds = [] } =
    await getEncryptedKeyMiddleware({
      type: GetKeysType.BuildKey,
      ...(buildId ? { relation_id: buildId } : {}),
    })

  if (
    (!serverBuildKeys || serverBuildKeys.length === 0) &&
    missingBuilds.length === 0
  ) {
    return []
  }

  const localBuildKeys: Record<string, string> = JSON.parse(
    localStorage.getItem(EncryptionKeys.buildAdditionalInfoEncryptionKeys) ||
      "{}"
  )
  const failedKeys: FailedKey[] = []

  for (const entry of serverBuildKeys) {
    const entryId = entry.id
    const entryName = entry.name || ""
    const serverDecryptedKey = await decryptBuildKeyValue({
      encryptedKey: entry.encrypted_key,
      additionalId: entry.additional_id,
      privateKeyPem,
    })

    if (!serverDecryptedKey) {
      failedKeys.push({
        id: entryId,
        name: entryName,
        reason: "WRONG_FORMAT",
        keyType: "build",
      })
      continue
    }

    const localKey = localBuildKeys[entryId]
    if (!localKey) {
      failedKeys.push({
        id: entryId,
        name: entryName,
        reason: "MISSING",
        keyType: "build",
      })
    } else if (localKey !== serverDecryptedKey) {
      failedKeys.push({
        id: entryId,
        name: entryName,
        reason: "WRONG_VALUE",
        keyType: "build",
      })
    }
  }

  for (const mb of missingBuilds) {
    const hasLocalBuildKey = !!localBuildKeys[mb.id]
    failedKeys.push({
      id: mb.id,
      name: mb.name,
      reason: hasLocalBuildKey ? "OUT_OF_SYNC" : "NO_SERVER_KEY",
      keyType: "build",
    })
  }

  return failedKeys
}

// ---------------------------------------------------------------------------
// Auto-fix helpers — called during recovery
// ---------------------------------------------------------------------------

/** For MISSING / WRONG_VALUE: write the server's decrypted key to localStorage */
export const fixLocalProjectKey = (
  projectId: string,
  serverDecryptedKey: string
) => {
  const existing: Record<string, string> = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  existing[projectId] = serverDecryptedKey
  localStorage.setItem(
    EncryptionKeys.projectEncryptionKeys,
    JSON.stringify(existing)
  )
}

/**
 * Auto-fix a single project key: fetch from server, decrypt, save to localStorage.
 * Called once per failed key so the user can see per-key progress.
 */
export const autoFixProjectKey = async (
  projectId: string,
  userId: string
): Promise<{ success: boolean }> => {
  try {
    const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""

    const privateKeyStores = await getStoreData<{
      id: string
      privateKey: string
    }>(Stores.PrivateKeys)
    const privateKeyPem =
      privateKeyStores.find((el) => el.id === userId)?.privateKey || ""
    if (!privateKeyPem) return { success: false }

    const privateKeyFromPem = getPrivateKeyFromPem(privateKeyPem)

    // Fetch only the key for this specific project
    const { data: projectKeys } = await getEncryptedKeyMiddleware({
      type: GetKeysType.ProjectKey,
      relation_id: projectId,
    })

    if (
      !projectKeys ||
      projectKeys.length === 0 ||
      !projectKeys[0]?.encrypted_key
    ) {
      return { success: false }
    }

    const entry = projectKeys[0]
    let serverDecryptedKey: string | null = null

    if (entry.type === KeyEncryptionType.ProjectShareKey) {
      try {
        const result = privateKeyFromPem.decrypt(
          entry.encrypted_key,
          "RSA-OAEP"
        )
        if (result && result !== entry.encrypted_key) {
          serverDecryptedKey = result
        }
      } catch {
        // decrypt failed
      }
    } else {
      const result = (await encryptionGCMHelper.decryptContext(
        vaultKey,
        entry.encrypted_key
      )) as string
      if (result && result !== entry.encrypted_key) {
        serverDecryptedKey = result
      }
    }

    if (!serverDecryptedKey) return { success: false }

    fixLocalProjectKey(projectId, serverDecryptedKey)
    return { success: true }
  } catch {
    return { success: false }
  }
}

export const autoFixBuildKey = async (
  buildId: string,
  userId: string,
  workspaceId?: string
): Promise<{ success: boolean }> => {
  try {
    const privateKeyStores = await getStoreData<{
      id: string
      privateKey: string
    }>(Stores.PrivateKeys)
    const privateKeyPem =
      privateKeyStores.find((el) => el.id === userId)?.privateKey || ""
    if (!privateKeyPem) return { success: false }

    const { data: serverBuildKeys } = await getEncryptedKeyMiddleware(
      {
        type: GetKeysType.BuildKey,
        relation_id: buildId,
      },
      workspaceId
    )
    const buildEntry = (serverBuildKeys || []).find(
      (k: any) => k.id === buildId
    )
    if (!buildEntry?.encrypted_key) {
      return { success: false }
    }
    const serverDecryptedKey = await decryptBuildKeyValue({
      encryptedKey: buildEntry.encrypted_key,
      additionalId: buildEntry.additional_id,
      privateKeyPem,
    })
    if (!serverDecryptedKey) return { success: false }
    const existing: Record<string, string> = JSON.parse(
      localStorage.getItem(EncryptionKeys.buildAdditionalInfoEncryptionKeys) ||
        "{}"
    )
    existing[buildId] = serverDecryptedKey
    localStorage.setItem(
      EncryptionKeys.buildAdditionalInfoEncryptionKeys,
      JSON.stringify(existing)
    )
    return { success: true }
  } catch {
    return { success: false }
  }
}

/** For OUT_OF_SYNC: remove stale key from localStorage */
export const removeStaleProjectKey = (projectId: string) => {
  const existing: Record<string, string> = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  delete existing[projectId]
  localStorage.setItem(
    EncryptionKeys.projectEncryptionKeys,
    JSON.stringify(existing)
  )
}

const fetchConversationKeyData = async (
  conversationId: string,
  workspaceId?: string
): Promise<{
  data: ConversationKeyResponse
  missingConversations: MissingConversation[]
} | null> => {
  try {
    const res = await Axios.get(`/api/auth/keys?loading=0`, {
      params: {
        type: GetKeysType.ConversationKey,
        relation_id: conversationId,
      },
      headers: workspaceId
        ? {
            "x-db-key": workspaceId,
          }
        : undefined,
    })
    const result = {
      data: res.data.data as ConversationKeyResponse,
      missingConversations: (res.data.missingConversations ||
        []) as MissingConversation[],
    }
    return result
  } catch {
    return null
  }
}

const decryptConversationKeyValue = async (params: {
  entry: ConversationKeyServerEntry
  serverProjectKeys: ProjectKeyServerEntry[]
  localProjectKeys: Record<string, string>
  vaultKey: string
  privateKeyPem: string
}): Promise<string | null> => {
  const {
    entry,
    serverProjectKeys,
    localProjectKeys,
    vaultKey,
    privateKeyPem,
  } = params
  const privateKeyFromPem = getPrivateKeyFromPem(privateKeyPem)

  // Step 1 — project key từ localStorage (decrypted sẵn)
  if (entry.additional_id) {
    const localProjectKey = localProjectKeys[entry.additional_id]
    if (localProjectKey) {
      try {
        const result = (await encryptionGCMHelper.decryptContext(
          localProjectKey,
          entry.encrypted_key
        )) as string
        if (result && result !== entry.encrypted_key) return result
      } catch {
        /* fall through */
      }
    }

    // Step 2 — project key từ server (cần decrypt trước)
    const serverPK = serverProjectKeys.find(
      (pk) => pk.projectId === entry.additional_id
    )
    if (serverPK) {
      let decryptedProjectKey: string | null = null
      if (serverPK.type === KeyEncryptionType.ProjectShareKey) {
        try {
          const result = privateKeyFromPem.decrypt(
            serverPK.encrypted_key,
            "RSA-OAEP"
          )
          if (result && result !== serverPK.encrypted_key)
            decryptedProjectKey = result
        } catch {
          /* fall through */
        }
      } else {
        try {
          const result = (await encryptionGCMHelper.decryptContext(
            vaultKey,
            serverPK.encrypted_key
          )) as string
          if (result && result !== serverPK.encrypted_key)
            decryptedProjectKey = result
        } catch {
          /* fall through */
        }
      }
      if (decryptedProjectKey) {
        try {
          const result = (await encryptionGCMHelper.decryptContext(
            decryptedProjectKey,
            entry.encrypted_key
          )) as string
          if (result && result !== entry.encrypted_key) return result
        } catch {
          /* fall through */
        }
      }
    }
  }

  // Step 3 — Fallback: RSA-OAEP private key trực tiếp
  try {
    const result = privateKeyFromPem.decrypt(entry.encrypted_key, "RSA-OAEP")
    if (result && result !== entry.encrypted_key) return result
  } catch {
    /* decrypt failed */
  }

  return null
}

export const autoFixConversationKey = async (
  conversationId: string,
  userId: string,
  workspaceId?: string
): Promise<{ success: boolean }> => {
  try {
    const privateKeyStores = await getStoreData<{
      id: string
      privateKey: string
    }>(Stores.PrivateKeys)
    const privateKeyPem =
      privateKeyStores.find((el) => el.id === userId)?.privateKey || ""
    if (!privateKeyPem) return { success: false }

    const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
    const localProjectKeys: Record<string, string> = JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    )

    const serverData = await fetchConversationKeyData(
      conversationId,
      workspaceId
    )
    if (!serverData) return { success: false }

    const { data } = serverData
    const targetEntry = (data.conversationKeys || []).find(
      (k) => k.conversation_id === conversationId
    )
    if (!targetEntry?.encrypted_key) return { success: false }

    const decryptedKey = await decryptConversationKeyValue({
      entry: targetEntry,
      serverProjectKeys: data.projectKeys || [],
      localProjectKeys,
      vaultKey,
      privateKeyPem,
    })
    if (!decryptedKey) return { success: false }

    const existing: Record<string, string> = JSON.parse(
      localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
    )
    existing[conversationId] = decryptedKey
    localStorage.setItem(
      EncryptionKeys.conversationEncryptionKeys,
      JSON.stringify(existing)
    )
    return { success: true }
  } catch {
    return { success: false }
  }
}

/** Report terminal failure to Sentry */
export const reportKeyHealthFailure = (
  errorCode: string,
  failedKeys: FailedKey[]
) => {
  addBreadcrumb("key-health", "Retry exhausted — capturing failure", "error", {
    failed_count: failedKeys.length,
  })
  captureKeyEvent(`Key health check failed - ${errorCode}`, {
    error_category: "KEY",
    error_type: "KEY_HEALTH_FAILURE",
    ...getSentryUserContext(),
    error_code: errorCode,
    step_trail: "PageLayout → KeyHealthDetailModal → retry exhausted",
    keys_failed: failedKeys.length,
    failed_keys: failedKeys.map((k) => ({ id: k.id, reason: k.reason })),
  })
}
